import type { ReactElement } from 'react';
import styled from '@emotion/styled';
import { moment } from '@hotjar/webapp/lib/moment';
import { useTranslation, Trans } from 'react-i18next';
import { css } from '@emotion/react';

import { tokens, Stack, Icon, Link, Tooltip } from '@hotjar/hotsauce';
import type { OwnedAccount } from '@hotjar/account';
import type { Site } from '@hotjar/site';
import { isPlanLocked } from '@hotjar/site';

import { DetailSize } from '@hotjar/webapp/features/Billing/SiteBox//components/DetailSize';
import { SiteLockedIcon } from '@hotjar/webapp/features/Billing/SiteBox/SiteLockedIcon';
import { useGetSiteProducts } from '@hotjar/webapp/domains/common/product/hooks/useGetSiteProducts';
import { useProductAllowances } from '@hotjar/webapp/domains/revenue/hooks/usePlanAllowances';
import { abbrNumber } from '@hotjar/insights-common/lib/number';

interface Props {
  account: OwnedAccount;
  site: Site;
  children: ReactElement;
  isTwoSkusActionRequired?: boolean;
}

const S = {
  AllowanceLabel: styled(DetailSize)`
    padding-top: ${tokens.spacer.x3};
  `,
  AdditionalInfo: styled.span`
    ${tokens.text('caption')};
    color: ${tokens.color.text_light};
  `,
};

const PRICING_MODEL_CHANGES_URL = 'https://www.hotjar.com/blog/updating-our-pricing-model/';

const firstOfJuly2022 = moment('2022-07-01');

// Action required deadline is:
// - 1st of July for monthly plans
// - billing cycle date for annual plans but the first billing cycle date on or after July 1st
function getEndOfGracePeriod(account: OwnedAccount) {
  if (account.billingCycle === 'monthly' || !account.nextBillingDate) {
    return firstOfJuly2022.format('DD MMMM YYYY');
  }

  const billingCycle = moment.unix(account.nextBillingDate);
  const endDate = billingCycle.isBefore(firstOfJuly2022)
    ? billingCycle.add(1, 'year')
    : billingCycle;
  return endDate.format('DD MMMM YYYY');
}

export const MultiSkuAllowance = ({
  account,
  site,
  children,
  isTwoSkusActionRequired,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const { getAllowances } = useProductAllowances();
  const { analyticsProduct, feedbackProduct } = useGetSiteProducts(site);

  const siteLocked = isPlanLocked(site, account);
  const endOfGracePeriod = getEndOfGracePeriod(account);

  if (!feedbackProduct || !analyticsProduct) {
    return null;
  }

  const activeActionRequired =
    isTwoSkusActionRequired && feedbackProduct.sku === 'feedback_basic_20';

  return (
    <div>
      {children}

      <Stack direction="column" spacing="x4pt5">
        <S.AllowanceLabel>
          {t('billing:site-box.cc-daily-allowance', {
            ccDailyAllowance: abbrNumber(analyticsProduct.dailyCcSessions),
          })}
          {siteLocked && <SiteLockedIcon />}
        </S.AllowanceLabel>
        <S.AllowanceLabel
          css={css`
            margin-top: unset;
            padding-top: ${isTwoSkusActionRequired ? tokens.spacer.x2 : tokens.spacer.x3};
          `}
        >
          {activeActionRequired && (
            <>
              {t('revenue:migrate-to-two-skus.allowance')} {siteLocked && <SiteLockedIcon />}
              <br />
              <Tooltip
                text={
                  siteLocked
                    ? t('revenue:migrate-to-two-skus.grace-period-locked', { endOfGracePeriod })
                    : t('revenue:migrate-to-two-skus.grace-period-regular', { endOfGracePeriod })
                }
              >
                <Icon icon="info" inline />
              </Tooltip>{' '}
              <S.AdditionalInfo>
                <Trans i18nKey="revenue:migrate-to-two-skus.grace-period-info">
                  <span>Until {{ endOfGracePeriod }},</span>
                  <Link href={PRICING_MODEL_CHANGES_URL} kind="inline">
                    learn more
                  </Link>
                </Trans>
              </S.AdditionalInfo>
            </>
          )}
          {!activeActionRequired && (
            <>
              {getAllowances(feedbackProduct).allowanceLabel}
              {siteLocked && <SiteLockedIcon />}
            </>
          )}
        </S.AllowanceLabel>
      </Stack>
    </div>
  );
};
